import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import apiService from "../../services/api";

const EMPTY_FORM = { mentorId: "", effectiveYearMonth: "", notes: "" };

const sharedStyles = {
  overlay: {
    position: "fixed",
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 1000, padding: "1rem",
  },
  modal: {
    backgroundColor: "white", borderRadius: "0.5rem", padding: "2rem",
    maxWidth: "800px", width: "100%", maxHeight: "90vh", overflow: "auto",
    boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)",
  },
  header: {
    display: "flex", justifyContent: "space-between", alignItems: "flex-start",
    marginBottom: "1.5rem", borderBottom: "1px solid #f1f5f9", paddingBottom: "1rem",
  },
  title: { margin: 0, color: "#001272", fontSize: "1.5rem", fontWeight: "600" },
  closeButton: {
    background: "none", border: "none", fontSize: "1.5rem", cursor: "pointer",
    color: "#6b7280", padding: 0, width: "32px", height: "32px",
    display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "0.25rem",
  },
  section: { marginBottom: "2rem" },
  sectionTitle: { fontSize: "1.125rem", fontWeight: "600", color: "#001272", marginBottom: "1rem" },
  infoCard: {
    backgroundColor: "#f0f9ff", border: "1px solid #bae6fd",
    borderRadius: "0.5rem", padding: "1.5rem", marginBottom: "1.5rem",
  },
  infoMain: { fontSize: "1.25rem", fontWeight: "600", color: "#001272", marginBottom: "0.5rem" },
  infoSub: { fontSize: "0.875rem", color: "#6b7280", marginBottom: "0.25rem" },
  form: {
    backgroundColor: "#f8fafc", border: "1px solid #e2e8f0",
    borderRadius: "0.5rem", padding: "1.5rem", marginBottom: "1.5rem",
  },
  formGroup: { marginBottom: "1rem" },
  label: { display: "block", fontSize: "0.875rem", fontWeight: "500", color: "#001272", marginBottom: "0.25rem" },
  input: {
    width: "100%", padding: "0.5rem 0.75rem", border: "1px solid #d1d5db",
    borderRadius: "0.375rem", fontSize: "0.875rem", outline: "none",
    boxSizing: "border-box",
  },
  textarea: {
    width: "100%", padding: "0.5rem 0.75rem", border: "1px solid #d1d5db",
    borderRadius: "0.375rem", fontSize: "0.875rem", minHeight: "80px",
    resize: "vertical", outline: "none", boxSizing: "border-box",
  },
  formActions: { display: "flex", gap: "0.75rem", justifyContent: "flex-end" },
  btn: { padding: "0.5rem 1rem", border: "none", borderRadius: "0.375rem", fontSize: "0.875rem", fontWeight: "500", cursor: "pointer" },
  btnPrimary: { backgroundColor: "#001272", color: "white" },
  btnSecondary: { backgroundColor: "#6b7280", color: "white" },
  btnDanger: { backgroundColor: "#dc2626", color: "white" },
  btnOutline: { backgroundColor: "transparent", color: "#001272", border: "1px solid #001272" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: "0.875rem" },
  th: {
    backgroundColor: "#f8fafc", border: "1px solid #e2e8f0", padding: "0.75rem",
    textAlign: "left", fontSize: "0.75rem", fontWeight: "600", color: "#64748b",
    textTransform: "uppercase", letterSpacing: "0.05em",
  },
  td: { border: "1px solid #e2e8f0", padding: "0.75rem", color: "#4b5563", verticalAlign: "top" },
  badge: { display: "inline-block", padding: "0.25rem 0.5rem", borderRadius: "0.25rem", fontSize: "0.75rem", fontWeight: "500" },
  actionBtns: { display: "flex", gap: "0.5rem" },
  spinner: {
    border: "3px solid #f3f3f3", borderTop: "3px solid #53bdeb",
    borderRadius: "50%", width: "40px", height: "40px",
    animation: "spin 1s linear infinite", marginBottom: "1rem",
  },
  loading: { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "2rem", color: "#6b7280" },
  error: { backgroundColor: "#fee2e2", border: "1px solid #fca5a5", borderRadius: "0.375rem", padding: "1rem", color: "#991b1b", marginBottom: "1rem" },
  emptyState: {
    textAlign: "center", padding: "2rem", color: "#6b7280",
    backgroundColor: "#f8fafc", borderRadius: "0.5rem", border: "1px dashed #e2e8f0",
  },
};

const statusColors = {
  Current: { bg: "#dcfce7", text: "#166534" },
  Future:  { bg: "#fef3c7", text: "#92400e" },
  Past:    { bg: "#f3f4f6", text: "#6b7280" },
};

const getStatus = (assignment) => {
  if (assignment.isCurrentAssignment) return "Current";
  if (assignment.isFutureAssignment) return "Future";
  return "Past";
};

const formatDate = (s) => {
  if (!s) return "N/A";
  try { return new Date(s).toLocaleDateString("en-GB", { year: "numeric", month: "short", day: "numeric" }); }
  catch { return s; }
};

const MentorAssignmentManager = ({ tutorId, tutorName, onClose }) => {
  const queryClient = useQueryClient();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [tutorSearch, setTutorSearch] = useState("");

  // Assignment history
  const { data: history = [], isLoading: histLoading, error: histError } = useQuery({
    queryKey: ["mentorAssignmentHistory", tutorId],
    queryFn: async () => {
      try { return await apiService.getMentorAssignmentHistory(tutorId); }
      catch { return []; }
    },
    enabled: !!tutorId,
  });

  // Current assignment
  const { data: current, isLoading: curLoading } = useQuery({
    queryKey: ["currentMentorAssignment", tutorId],
    queryFn: async () => {
      try { return await apiService.getCurrentMentorAssignment(tutorId); }
      catch { return null; }
    },
    enabled: !!tutorId,
  });

  // All tutors for mentor picker
  const { data: tutorsData } = useQuery({
    queryKey: ["tutors", 0, 500],
    queryFn: () => apiService.getTutors(0, 500),
    staleTime: 5 * 60 * 1000,
  });

  const allTutors = (() => {
    if (!tutorsData) return [];
    if (Array.isArray(tutorsData)) return tutorsData;
    if (tutorsData.content) return tutorsData.content;
    return [];
  })();

  const filteredTutors = allTutors.filter((t) => {
    if (t.canonicalTutorId === tutorId) return false; // can't be own mentor
    if (!tutorSearch) return true;
    const name = `${t.firstName || ""} ${t.lastName || ""} ${t.email || ""}`.toLowerCase();
    return name.includes(tutorSearch.toLowerCase());
  });

  const invalidate = () => {
    queryClient.refetchQueries({ queryKey: ["mentorAssignmentHistory", tutorId] });
    queryClient.invalidateQueries({ queryKey: ["tutors"] });
  };

  const createMutation = useMutation({
    mutationFn: (data) => apiService.createMentorAssignment({ ...data, tutorId }),
    onSuccess: () => { toast.success("Assignment created"); invalidate(); resetForm(); },
    onError: (e) => toast.error(`Failed: ${e.message}`),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => apiService.updateMentorAssignment(id, { ...data, tutorId }),
    onSuccess: () => { toast.success("Assignment updated"); invalidate(); resetForm(); },
    onError: (e) => toast.error(`Failed: ${e.message}`),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => apiService.deleteMentorAssignment(id),
    onSuccess: () => { toast.success("Assignment deleted"); invalidate(); },
    onError: (e) => toast.error(`Failed: ${e.message}`),
  });

  const resetForm = () => { setIsFormOpen(false); setEditingId(null); setForm(EMPTY_FORM); setTutorSearch(""); };

  const handleEdit = (a) => {
    setEditingId(a.id);
    setForm({ mentorId: a.mentorId, effectiveYearMonth: a.effectiveYearMonth, notes: a.notes || "" });
    setIsFormOpen(true);
  };

  const handleSubmit = () => {
    if (!form.mentorId || !form.effectiveYearMonth) {
      toast.error("Please select a mentor and effective month");
      return;
    }
    if (editingId) updateMutation.mutate({ id: editingId, data: form });
    else createMutation.mutate(form);
  };

  const isMutating = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;
  const isLoading = histLoading || curLoading;

  return (
    <div style={sharedStyles.overlay}>
      <div style={sharedStyles.modal}>
        <div style={sharedStyles.header}>
          <h2 style={sharedStyles.title}>Mentor for {tutorName}</h2>
          <button style={sharedStyles.closeButton} onClick={onClose} disabled={isMutating}>×</button>
        </div>

        {isLoading ? (
          <div style={sharedStyles.loading}>
            <div style={sharedStyles.spinner} />
            <p>Loading...</p>
          </div>
        ) : (
          <>
            {/* Current mentor */}
            <div style={sharedStyles.section}>
              <h3 style={sharedStyles.sectionTitle}>Current Mentor</h3>
              {current ? (
                <div style={sharedStyles.infoCard}>
                  <div style={sharedStyles.infoMain}>
                    {current.mentorFirstName} {current.mentorLastName}
                  </div>
                  <div style={sharedStyles.infoSub}>{current.mentorEmail}</div>
                  <div style={sharedStyles.infoSub}>Effective: {current.effectiveYearMonth}</div>
                  {current.notes && <div style={sharedStyles.infoSub}>Notes: {current.notes}</div>}
                </div>
              ) : (
                <div style={sharedStyles.emptyState}>No mentor currently assigned.</div>
              )}
            </div>

            {/* Form */}
            <div style={sharedStyles.section}>
              {editingId && <h3 style={sharedStyles.sectionTitle}>Edit Assignment</h3>}
              {isFormOpen ? (
                <div style={sharedStyles.form}>
                  <div style={sharedStyles.formGroup}>
                    <label style={sharedStyles.label}>Search Mentor *</label>
                    <input
                      style={sharedStyles.input}
                      placeholder="Type to search tutors..."
                      value={tutorSearch}
                      onChange={(e) => setTutorSearch(e.target.value)}
                    />
                  </div>
                  <div style={sharedStyles.formGroup}>
                    <label style={sharedStyles.label}>Select Mentor *</label>
                    <select
                      style={sharedStyles.input}
                      value={form.mentorId}
                      onChange={(e) => setForm((f) => ({ ...f, mentorId: e.target.value }))}
                      disabled={isMutating}
                    >
                      <option value="">— select a tutor —</option>
                      {filteredTutors.map((t) => (
                        <option key={t.canonicalTutorId} value={t.canonicalTutorId}>
                          {t.firstName} {t.lastName} ({t.email})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div style={sharedStyles.formGroup}>
                    <label style={sharedStyles.label}>Effective Year-Month *</label>
                    <input
                      type="month"
                      style={sharedStyles.input}
                      value={form.effectiveYearMonth}
                      onChange={(e) => setForm((f) => ({ ...f, effectiveYearMonth: e.target.value }))}
                      disabled={isMutating}
                    />
                  </div>
                  <div style={sharedStyles.formGroup}>
                    <label style={sharedStyles.label}>Notes</label>
                    <textarea
                      style={sharedStyles.textarea}
                      value={form.notes}
                      onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                      placeholder="Optional notes..."
                      disabled={isMutating}
                    />
                  </div>
                  <div style={sharedStyles.formActions}>
                    <button style={{ ...sharedStyles.btn, ...sharedStyles.btnSecondary }} onClick={resetForm} disabled={isMutating}>Cancel</button>
                    <button style={{ ...sharedStyles.btn, ...sharedStyles.btnPrimary }} onClick={handleSubmit} disabled={isMutating}>
                      {isMutating ? "Saving..." : editingId ? "Update" : "Create"}
                    </button>
                  </div>
                </div>
              ) : (
                <div style={sharedStyles.formActions}>
                  <button style={{ ...sharedStyles.btn, ...sharedStyles.btnOutline }} onClick={() => setIsFormOpen(true)}>
                    Assign Mentor
                  </button>
                </div>
              )}
            </div>

            {/* History table */}
            <div style={sharedStyles.section}>
              <h3 style={sharedStyles.sectionTitle}>Mentor History</h3>
              {histError ? (
                <div style={sharedStyles.error}>Error loading history: {histError.message}</div>
              ) : history.length === 0 ? (
                <div style={sharedStyles.emptyState}>No assignment history.</div>
              ) : (
                <table style={sharedStyles.table}>
                  <thead>
                    <tr>
                      <th style={sharedStyles.th}>Effective</th>
                      <th style={sharedStyles.th}>Mentor</th>
                      <th style={sharedStyles.th}>Status</th>
                      <th style={sharedStyles.th}>Notes</th>
                      <th style={sharedStyles.th}>Created</th>
                      <th style={sharedStyles.th}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((a) => {
                      const status = getStatus(a);
                      const colors = statusColors[status];
                      return (
                        <tr key={a.id}>
                          <td style={sharedStyles.td}>{a.effectiveYearMonth}</td>
                          <td style={sharedStyles.td}>{a.mentorFirstName} {a.mentorLastName}</td>
                          <td style={sharedStyles.td}>
                            <span style={{ ...sharedStyles.badge, backgroundColor: colors.bg, color: colors.text }}>{status}</span>
                          </td>
                          <td style={sharedStyles.td}>{a.notes || "—"}</td>
                          <td style={sharedStyles.td}>{formatDate(a.createdAt)}</td>
                          <td style={sharedStyles.td}>
                            <div style={sharedStyles.actionBtns}>
                              <button style={{ ...sharedStyles.btn, ...sharedStyles.btnOutline }} onClick={() => handleEdit(a)} disabled={isMutating}>Edit</button>
                              <button
                                style={{ ...sharedStyles.btn, ...sharedStyles.btnDanger }}
                                onClick={() => window.confirm("Delete this assignment?") && deleteMutation.mutate(a.id)}
                                disabled={isMutating}
                              >Delete</button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}

        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );
};

export default MentorAssignmentManager;
